/* eslint-disable react-refresh/only-export-components -- o hook de registro mora com a bandeja que o lê */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router';
import { BellPlus, BellRing, ChevronDown } from 'lucide-react';
import styles from './NoticeTray.module.css';

/* ==========================================================================
   Resumo dos avisos do topo (relatório de UX, P1-11, P1-12 e P2-14; decisão
   D3 do dono)

   As faixas globais do painel (impressão, clientes esperando, estado do
   Atendente IA, WhatsApp, atendimentos, teste gratuito e entrega) chegaram a
   prender 334 de 844 px do celular. Agora o topo mostra uma linha só, presa
   abaixo da barra, com o aviso que não pode esperar e "N avisos ▾", que abre
   a lista inteira, no celular e no desktop.

   Cada faixa continua dona de quando aparece, do texto e das ações. Ela só
   conta ao resumo o que mostra, por `useTrayNotices`, com um texto curto e o
   nível:
   - `urgent` (cliente prioritário esperando, cancelamento sem resposta, o
     papel que não sai agora, WhatsApp desconectado): fica à vista no resumo,
     todos juntos e sem corte em nenhuma largura, e pinta a linha de vermelho;
   - `normal` (o chamado para agir: cliente esperando, solicitação com prazo
     correndo, cupom que pode não ter saído): com menos de dois urgentes, o
     resumo se completa com eles até dois avisos à vista, e só o que vem
     depois do urgente pode ser cortado com reticências;
   - `info` (teste gratuito, impressão pausada, configuração da impressão
     pendente): só aparece no resumo quando não há outro. O estado que dura
     dias não tira da linha quem está esperando agora.

   A faixa que precisa de um gesto da pessoa para funcionar com a lista
   fechada (o "Ativar som e avisos" do atendimento, que pede ao navegador a
   permissão de notificação) o registra por `useTrayAction`, e o botão aparece
   ao lado do resumo enquanto a lista está fechada.

   As faixas ficam montadas mesmo com a lista fechada: o som, o título "(N) ",
   a notificação e as leituras continuam rodando. Fora da bandeja (os testes de
   cada faixa), o registro não faz nada e a faixa aparece como sempre.
   ========================================================================== */

export type TrayNoticeLevel = 'urgent' | 'normal' | 'info';

export interface TrayNotice {
  /**
   * Única na faixa que registra. Mudar a chave anuncia o resumo de novo ao
   * leitor de tela: ponha nela o que conta (a contagem), e não o que só passa
   * (a espera "há 7 min").
   */
  key: string;
  /** O texto curto do resumo. */
  text: string;
  level: TrayNoticeLevel;
}

/** O gesto que a faixa pede ao lado do resumo, com a lista fechada. */
interface TrayAction {
  label: string;
  run: () => void;
}

type Report = (slotId: string, order: number, notices: readonly TrayNotice[]) => void;
type ReportAction = (slotId: string, order: number, action: TrayAction | null) => void;

interface TrayReporters {
  notices: Report;
  action: ReportAction;
}

const ReportContext = createContext<TrayReporters | null>(null);

interface Slot {
  id: string;
  order: number;
}

const SlotContext = createContext<Slot | null>(null);

/**
 * O que a faixa mostra agora, para o resumo do topo. Chame sempre, antes de
 * qualquer `return` antecipado, com a lista vazia quando a faixa não aparece.
 */
export function useTrayNotices(notices: readonly TrayNotice[]): void {
  const report = useContext(ReportContext)?.notices;
  const slot = useContext(SlotContext);
  // O conteúdo, e não a referência: a faixa monta a lista a cada render.
  const serialized = JSON.stringify(notices);
  useEffect(() => {
    if (!report || !slot) return;
    report(slot.id, slot.order, JSON.parse(serialized) as TrayNotice[]);
  }, [report, slot, serialized]);
  useEffect(() => {
    if (!report || !slot) return;
    return () => report(slot.id, slot.order, []);
  }, [report, slot]);
}

/**
 * O botão que a faixa pede ao lado do resumo enquanto a lista está fechada
 * ("Ativar som e avisos"), ou nulo quando não há o que pedir. O clique chama
 * `onActivate`, o mesmo gesto do botão da faixa. Chame sempre, antes de
 * qualquer `return` antecipado.
 */
export function useTrayAction(label: string | null, onActivate: () => void): void {
  const report = useContext(ReportContext)?.action;
  const slot = useContext(SlotContext);
  const handler = useRef(onActivate);
  useEffect(() => {
    handler.current = onActivate;
  });
  // Estável: o resumo só renderiza de novo quando o rótulo muda.
  const run = useCallback(() => handler.current(), []);
  useEffect(() => {
    if (!report || !slot) return;
    report(slot.id, slot.order, label ? { label, run } : null);
  }, [report, slot, label, run]);
  useEffect(() => {
    if (!report || !slot) return;
    return () => report(slot.id, slot.order, null);
  }, [report, slot]);
}

export interface NoticeTraySlot {
  /** Estável: identifica a faixa no resumo. */
  id: string;
  node: ReactNode;
}

export interface NoticeTrayProps {
  /** As faixas, na ordem em que aparecem na lista aberta. */
  slots: readonly NoticeTraySlot[];
}

interface SlotNotices {
  order: number;
  notices: readonly TrayNotice[];
}

interface SlotAction {
  order: number;
  action: TrayAction;
}

/** "1 aviso", "3 avisos". */
export function noticeCountLabel(count: number): string {
  return count === 1 ? '1 aviso' : `${count} avisos`;
}

/** Quantos avisos o resumo mostra, no mínimo, quando há urgente ou chamado para agir. */
export const FEATURED_MINIMUM = 2;

/**
 * O que o resumo mostra: os urgentes, todos; com menos de dois, completa até
 * dois com os chamados para agir, depois dos urgentes; sem nenhum dos dois, o
 * primeiro da lista (o lembrete).
 */
export function featuredNotices(notices: readonly TrayNotice[]): readonly TrayNotice[] {
  const urgent = notices.filter((notice) => notice.level === 'urgent');
  const normal = notices.filter((notice) => notice.level === 'normal');
  const featured = urgent.length >= FEATURED_MINIMUM ? urgent : [...urgent, ...normal].slice(0, FEATURED_MINIMUM);
  if (featured.length > 0) return featured;
  return notices[0] ? [notices[0]] : [];
}

export function NoticeTray({ slots }: NoticeTrayProps) {
  const [bySlot, setBySlot] = useState<Readonly<Record<string, SlotNotices>>>({});
  const [actions, setActions] = useState<Readonly<Record<string, SlotAction>>>({});
  const [open, setOpen] = useState(false);
  const listId = useId();
  const summaryId = useId();

  const reportNotices = useCallback<Report>((slotId, order, notices) => {
    setBySlot((current) => {
      const previous = current[slotId];
      if (!previous && notices.length === 0) return current;
      if (previous && previous.order === order && JSON.stringify(previous.notices) === JSON.stringify(notices)) {
        return current;
      }
      const next = { ...current };
      if (notices.length === 0) delete next[slotId];
      else next[slotId] = { order, notices };
      return next;
    });
  }, []);

  const reportAction = useCallback<ReportAction>((slotId, order, action) => {
    setActions((current) => {
      const previous = current[slotId];
      if (!previous && !action) return current;
      if (
        previous &&
        action &&
        previous.order === order &&
        previous.action.label === action.label &&
        previous.action.run === action.run
      ) {
        return current;
      }
      const next = { ...current };
      if (!action) delete next[slotId];
      else next[slotId] = { order, action };
      return next;
    });
  }, []);

  const reporters = useMemo<TrayReporters>(
    () => ({ notices: reportNotices, action: reportAction }),
    [reportNotices, reportAction],
  );

  const all = useMemo(
    () =>
      Object.values(bySlot)
        .sort((a, b) => a.order - b.order)
        .flatMap((slot) => slot.notices),
    [bySlot],
  );
  const count = all.length;
  const featured = featuredNotices(all);
  const summaryText = featured.map((notice) => notice.text).join(' · ');
  // Só urgentes à vista: o resumo cresce o que precisar, sem reticências (os textos urgentes são curtos).
  // Com um chamado para agir depois do urgente, as linhas têm limite, e só o fim, que não é urgente, se corta.
  const onlyUrgent = featured.length > 0 && featured.every((notice) => notice.level === 'urgent');
  const action = Object.values(actions).sort((a, b) => a.order - b.order)[0]?.action ?? null;
  const tone: TrayNoticeLevel = all.some((notice) => notice.level === 'urgent')
    ? 'urgent'
    : all.some((notice) => notice.level === 'normal')
      ? 'normal'
      : 'info';
  const countLabel = noticeCountLabel(count);

  // A lista fecha sozinha quando o último aviso some e ao trocar de tela: o
  // link de um aviso leva a outra página, que não deve abrir coberta por ela.
  const location = useLocation();
  const [seenLocation, setSeenLocation] = useState(location.key);
  if (seenLocation !== location.key) {
    setSeenLocation(location.key);
    if (open) setOpen(false);
  }
  if (open && count === 0) setOpen(false);
  const expanded = open && count > 0;

  // O leitor de tela ouve o resumo quando um aviso entra, sai ou muda de
  // contagem, e não a cada minuto da espera: o anúncio só é refeito quando as
  // chaves mudam.
  const signature = all.map((notice) => notice.key).join('|');
  const [announced, setAnnounced] = useState({ signature: '', text: '' });
  if (announced.signature !== signature) {
    setAnnounced({ signature, text: count > 0 ? `${countLabel}: ${summaryText}` : '' });
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape' || !expanded) return;
    event.preventDefault();
    setOpen(false);
    document.getElementById(summaryId)?.focus();
  };

  return (
    <ReportContext.Provider value={reporters}>
      <div className={styles.tray} data-testid="panel-notices" data-tone={tone} data-open={expanded} onKeyDown={onKeyDown}>
        {count > 0 && (
          <div className={styles.bar}>
            <button
              id={summaryId}
              type="button"
              className={styles.summary}
              aria-expanded={expanded}
              aria-controls={listId}
              data-testid="panel-notices-summary"
              onClick={() => setOpen((current) => !current)}
            >
              <BellRing size={16} className={styles.icon} aria-hidden="true" />
              <span className={styles.summaryText} data-clamp={onlyUrgent ? 'none' : 'lines'}>
                {summaryText}
              </span>
              <span className={styles.count}>
                {countLabel}
                <ChevronDown size={16} className={styles.chevron} aria-hidden="true" />
              </span>
            </button>
            {/* Aberta, a lista já mostra o botão na faixa dele. */}
            {action && !expanded && (
              <button
                type="button"
                className={styles.barAction}
                onClick={action.run}
                aria-label={action.label}
                title={action.label}
                data-testid="panel-notices-action"
              >
                <BellPlus size={16} aria-hidden="true" />
                <span className={styles.barActionLabel} aria-hidden="true">
                  {action.label}
                </span>
              </button>
            )}
          </div>
        )}
        <div id={listId} className={styles.list} hidden={!expanded} data-testid="panel-notices-list">
          {slots.map((slot, order) => (
            <NoticeSlot key={slot.id} id={slot.id} order={order}>
              {slot.node}
            </NoticeSlot>
          ))}
        </div>
        {/* `aria-live` sem `role="status"`: o anúncio não disputa com o `status` das telas. */}
        <span className="sr-only" aria-live="polite" aria-atomic="true" data-testid="panel-notices-announcer">
          {announced.text}
        </span>
      </div>
    </ReportContext.Provider>
  );
}

function NoticeSlot({ id, order, children }: { id: string; order: number; children: ReactNode }) {
  const slot = useMemo(() => ({ id, order }), [id, order]);
  return <SlotContext.Provider value={slot}>{children}</SlotContext.Provider>;
}
